import React, { useState } from 'react';
import { QuotationData } from '@/types';
import { quotationApi } from '@/services/api';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Download, 
  Send,
  User,
  Building,
  DollarSign,
  Package,
  Calendar,
  MessageSquare,
  Loader2,
  Mail
} from 'lucide-react';
import { clsx } from 'clsx';

interface QuotationPanelProps {
  quotationData: QuotationData;
  progress: number;
  missingFields: string[];
  isComplete: boolean;
  conversationId: string | null;
  emailNotificationEnabled?: boolean;
  onQuotationGenerated?: (results: any) => void;
}

export const QuotationPanel: React.FC<QuotationPanelProps> = ({
  quotationData,
  progress,
  missingFields,
  isComplete,
  conversationId,
  emailNotificationEnabled = true,
  onQuotationGenerated,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [isGeneratingQuote, setIsGeneratingQuote] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [quoteGenerated, setQuoteGenerated] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleExportQuotation = async () => {
    if (!conversationId) return;
    
    try {
      setIsExporting(true);
      const response = await quotationApi.exportQuotationData(conversationId);
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${conversationId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting quotation:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleGenerateQuotation = async () => {
    try {
      setIsGeneratingQuote(true);
      const response = await quotationApi.generateQuotation(quotationData);
      console.log('Quotation generated:', response);
      setQuoteGenerated(true);
      setTimeout(() => setQuoteGenerated(false), 5000);
      
      // Notify parent component
      if (onQuotationGenerated) {
        onQuotationGenerated(response);
      }
    } catch (error) {
      console.error('Error generating quotation:', error);
    } finally {
      setIsGeneratingQuote(false);
    }
  };

  const handleSendEmailQuotation = async () => {
    try {
      setIsSendingEmail(true);
      const response = await quotationApi.sendEmailQuotation(quotationData);
      console.log('Email quotation sent:', response);
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (error) {
      console.error('Error sending email quotation:', error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getFieldIcon = (field: string) => {
    switch (field.toLowerCase()) {
      case 'email': return <User size={16} />;
      case 'company': return <Building size={16} />;
      case 'budget': return <DollarSign size={16} />;
      case 'quantity': return <Package size={16} />;
      case 'occasion': return <Calendar size={16} />;
      case 'requirements': return <MessageSquare size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  const getProgressColor = () => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 66) return 'bg-blue-500';
    if (progress >= 33) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <FileText size={16} className="text-blue-600" />
          </div>
          <h2 className="font-semibold text-gray-900">Quotation Progress</h2>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Data Collection</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={clsx('h-2 rounded-full transition-all duration-300', getProgressColor())}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        <p className="text-sm text-gray-600">
          {isComplete 
            ? '✅ Ready for quotation!'
            : `${missingFields.length} field${missingFields.length !== 1 ? 's' : ''} remaining`
          }
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Collected Data */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 mb-3">Collected Information</h3>
          
          {/* Customer Email */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              quotationData.customer_email ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <User size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Customer Email</p>
              <p className="text-sm text-gray-600">
                {quotationData.customer_email || 'Not provided'}
              </p>
            </div>
            {quotationData.customer_email && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>

          {/* Company Name */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              quotationData.company_name ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <Building size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Company Name</p>
              <p className="text-sm text-gray-600">
                {quotationData.company_name || 'Not provided'}
              </p>
            </div>
            {quotationData.company_name && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>

          {/* Budget */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              (quotationData.budget_per_pack || quotationData.total_budget) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <DollarSign size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Budget</p>
              <p className="text-sm text-gray-600">
                {quotationData.budget_per_pack 
                  ? `$${quotationData.budget_per_pack} per pack`
                  : quotationData.total_budget 
                    ? `$${quotationData.total_budget} total`
                    : 'Not provided'
                }
              </p>
            </div>
            {(quotationData.budget_per_pack || quotationData.total_budget) && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>

          {/* Quantity */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              quotationData.number_of_packs ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <Package size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Quantity</p>
              <p className="text-sm text-gray-600">
                {quotationData.number_of_packs 
                  ? `${quotationData.number_of_packs} packs`
                  : 'Not provided'
                }
              </p>
            </div>
            {quotationData.number_of_packs && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>

          {/* Occasion */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              quotationData.occasion ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <Calendar size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Occasion</p>
              <p className="text-sm text-gray-600">
                {quotationData.occasion || 'Not provided'}
              </p>
            </div>
            {quotationData.occasion && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>

          {/* Special Requests */}
          <div className="flex items-center space-x-3">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              quotationData.special_requests ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
            )}>
              <MessageSquare size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Special Requests</p>
              <p className="text-sm text-gray-600">
                {quotationData.special_requests || 'Not provided'}
              </p>
            </div>
            {quotationData.special_requests && (
              <CheckCircle size={16} className="text-green-500" />
            )}
          </div>
        </div>

        {/* Missing Fields */}
        {missingFields.length > 0 && (
          <div className="mt-6">
            <h3 className="font-medium text-gray-900 mb-3">Still Needed</h3>
            <div className="space-y-2">
              {missingFields.map((field) => (
                <div key={field} className="flex items-center space-x-3 p-2 bg-yellow-50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                    {getFieldIcon(field)}
                  </div>
                  <span className="text-sm text-yellow-800">{field}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Mention these details in your chat to help Mary prepare your quotation faster!
              </p>
            </div>
          </div>
        )}

        {/* Completion Status */}
        {isComplete && (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-green-800 mb-2">
                <CheckCircle size={20} />
                <span className="font-medium">Ready for Quotation!</span>
              </div>
              <p className="text-sm text-green-700">
                All required information has been collected. Mary can now prepare your customized quotation.
              </p>
            </div>
            
            {/* Email Notification Message */}
            {emailNotificationEnabled && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2 text-blue-800 mb-2">
                  <Mail size={20} />
                  <span className="font-medium">Quotation Delivery</span>
                </div>
                <p className="text-sm text-blue-700">
                  <strong>Thank you for your inquiry!</strong> A formal quotation with detailed pricing, 
                  product specifications, and delivery information will be sent to your email address shortly.
                </p>
                <div className="mt-2 text-xs text-blue-600">
                  ✉️ Please check your email within 24 hours for the complete quotation document.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        {/* Export Button */}
        <button
          onClick={handleExportQuotation}
          disabled={isExporting || !conversationId}
          className={clsx(
            'w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors text-sm font-medium',
            'bg-blue-600 text-white hover:bg-blue-700',
            (isExporting || !conversationId) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isExporting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Download size={16} />
          )}
          <span>Export Data</span>
        </button>

        {/* Success Message */}
        {exportSuccess && (
          <div className="text-center text-sm text-green-600">
            ✅ Quotation data exported successfully!
          </div>
        )}

        {/* Generate Quote Button (when complete) */}
        {isComplete && (
          <div className="space-y-2">
            {/* Generate Quotation Button */}
            <button
              onClick={handleGenerateQuotation}
              disabled={isGeneratingQuote}
              className={clsx(
                'w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors text-sm font-medium',
                'bg-blue-600 text-white hover:bg-blue-700',
                isGeneratingQuote && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isGeneratingQuote ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <FileText size={16} />
              )}
              <span>Generate Quotation</span>
            </button>
            
            {/* Send Email Button */}
            <button
              onClick={handleSendEmailQuotation}
              disabled={isSendingEmail}
              className={clsx(
                'w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors text-sm font-medium',
                'bg-green-600 text-white hover:bg-green-700',
                isSendingEmail && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isSendingEmail ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              <span>Send Email Quotation</span>
            </button>
            
            {/* Success Messages */}
            {quoteGenerated && (
              <div className="text-center text-sm text-blue-600">
                ✅ Quotation generated successfully!
              </div>
            )}
            {emailSent && (
              <div className="text-center text-sm text-green-600">
                ✅ Email quotation sent successfully!
              </div>
            )}
            
            {emailNotificationEnabled && (
              <div className="text-center text-xs text-gray-500">
                ℹ️ The formal quotation will be emailed to {quotationData.customer_email}
              </div>
            )}
          </div>
        )}

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center">
          Your data is automatically saved during the conversation
        </p>
      </div>
    </div>
  );
};